import { useIntl } from "react-intl";
import type { Tier } from "shared/marker-modes";

type Props = {
  tier: Tier;
  /**
   * Whether a trial is what grants this tier. A trial and a subscription are
   * never both live (recomputeAndApplyTier ends the trial on purchase), so
   * this is simply `user.trial != null` at every call site.
   */
  onTrial?: boolean;
};

/**
 * Small rounded pill showing the user's tier. Anon users get nothing (no
 * session, no tier to surface). Tier labels live in i18n at `nav.tier.*` so
 * the caller doesn't think about copy. Same visual treatment across all
 * tiers - the label IS the differentiator.
 *
 * A trial says so. Without it the badge reads "PRO" for someone who has paid
 * nothing and whose access ends on a date - the same pill a paying customer
 * gets. The tier stays IN the label rather than being replaced by a bare
 * "TRIAL", because `trials.tier` already allows docs/api/pro and which one is
 * being trialled is worth keeping.
 */
export function TierBadge({ tier, onTrial = false }: Props) {
  const intl = useIntl();
  if (tier === "anon") return null;
  const label = intl.formatMessage({ id: `nav.tier.${tier}` });
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tracking-wide text-muted-fg">
      {onTrial ? intl.formatMessage({ id: "nav.tier.trial_badge" }, { tier: label }) : label}
    </span>
  );
}
